import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { PromiseAuditLog } from "@/types/collections";

interface PromiseAuditLogApi {
  id: number;
  promiseId: number;
  actionType: string;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  metadata: string | null;
  createdByUserId: number;
  createdByUserName: string;
  createdAt: string;
}

const actionTypeToActionMap: Record<string, PromiseAuditLog["action"]> = {
  promisecreated: "promise_created",
  statuschanged: "status_change",
  amountupdated: "amount_change",
  dateupdated: "date_change",
  confirmationmethodupdated: "field_update",
  noteadded: "note_added",
  casereassigned: "collection_reassignment",
  assigneduserupdated: "assigned_user_updated",
  transactionlinked: "transaction_linked",
  transactionunlinked: "transaction_unlinked",
  phonecalllogged: "phone_call_logged",
};

function mapApiPromiseAuditLog(api: PromiseAuditLogApi): PromiseAuditLog {
  const actionName = api.actionType.toLowerCase().replace(/\s+/g, "");
  return {
    id: String(api.id),
    promiseId: String(api.promiseId),
    action: actionTypeToActionMap[actionName] || "field_update",
    description: api.description,
    fieldName: api.fieldName || undefined,
    oldValue: api.oldValue,
    newValue: api.newValue,
    createdBy: api.createdByUserName || "System",
    createdAt: new Date(api.createdAt),
  };
}

export function usePromiseAuditLogs(promiseId: string | undefined, mockData: PromiseAuditLog[]) {
  const { apiGet } = useApi();

  return useModeAwareQuery<PromiseAuditLog[], Error>(
    ["promise-audit-logs", promiseId],
    async ({ signal }) => {
      if (!promiseId) return [];
      const response = await apiGet<PromiseAuditLogApi[]>(
        "/api/v1/audit-logs/promises",
        { promiseId },
        signal
      );
      return response.map(mapApiPromiseAuditLog);
    },
    mockData,
    { enabled: !!promiseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}
